<template>
  <div ref="tiptapWrapper" 
       :class="'i-tiptap-wrapper ' + (tiptapconfig.class ? tiptapconfig.class : '')" 
       :style="{ width: editorWidth, height: editorHeight }"
       >
    <menubar
      v-if="editor"
      ref="menubar"
      :editor="editor"
      @keydown.escape.prevent.stop="() => editor.commands.focus()"
      v-model:sourceMode="sourceMode"
      v-model:spellCheck="spellCheck"
      :config="tiptapconfig"
      :pShowImageModal="this.showImageModal"
      :pShowLinkModal="this.showLinkModal"
      v-on:toggleImageModal="this.showImageModal = $event"
      v-on:toggleLinkModal="this.showLinkModal = $event"
    />
    <TableMenu 
      v-if="tablePopover"
      :editor="editor"
    />
    <LinkPopover  
      v-if="linkPopover"
      :editor="editor" 
      ref="linkpopover" 
      v-on:toggleLinkModal="this.showLinkModal = $event"
    />
    <editor-content
      v-show="!sourceMode"
      @keydown.alt.enter.prevent.stop="() => focusToolbar()"
      @keydown.alt.f10.prevent.stop="() => focusToolbar()"
      @keydown.ctrl.k.prevent.stop="() => focusLink()"
      :spellcheck="spellCheck.toString()"
      class="i-tiptap-content-wrapper"
      :editor="editor"
    />
    <template v-if="sourceMode">
      <div v-if="tiptapconfig.sourceModeType === 'markdown'" class="i-tiptap-content-wrapper">
        <textarea class="i-tiptap-content html-source" spellcheck="false" v-model="markdownContent"> </textarea>
      </div>
      <div v-else class="i-tiptap-content-wrapper">
        <textarea class="i-tiptap-content html-source" spellcheck="false" v-model="sourceContent"> </textarea>
      </div>
    </template>
    <div class="i-tiptap-footer" tabindex="-1">
      <div class="button-group" v-if="this.tiptapconfig.canInspect">
        <i-button
          :disabled="this.sourceMode"
          :title="__(`Toggle Blocks`)"
          :class="[{ active: this.showDecorations }, 'i-btn btn']"
          @click.prevent="toggleDecorations"
        >
          <i-icon :icon="`eye`" type="far" size="small"></i-icon>
        </i-button>
      </div>
      <div class="selection_path">
        <span 
          class="node" 
          v-for="(node, index) in selectionPath" 
          :key="index" 
          @click="selectNode(node.pos)"
        > 
          {{ node.name }} 
        </span>
      </div>
      <span ref="resizeIcon" v-if="tiptapconfig.resizable" id="resize-icon" class="resize-icon">◢</span>
      <!-- Resize icon -->
    </div>
  </div>
</template>



<script>
import beautifyHtml from 'js-beautify/js/src/html'

import { Editor, EditorContent, extensions } from '@tiptap/vue-3'
import { BubbleMenu } from '@tiptap/vue-3/menus' // tiptap 3
import StarterKit from '@tiptap/starter-kit'
import { Markdown } from '@tiptap/markdown'

import { Document } from '@tiptap/extension-document'
import { Text } from '@tiptap/extension-text'
import { TextStyleKit } from '@tiptap/extension-text-style'
import { Underline } from '@tiptap/extension-underline'
import { Subscript } from '@tiptap/extension-subscript'
import { Superscript } from '@tiptap/extension-superscript'
import { Heading } from '@tiptap/extension-heading'
import { Paragraph } from '@tiptap/extension-paragraph'
import { ListItem } from '@tiptap/extension-list-item'
import { BulletList, OrderedList } from '@tiptap/extension-list' // tiptap3
import { Blockquote } from '@tiptap/extension-blockquote'
import { CodeBlock } from '@tiptap/extension-code-block'
import { Code } from '@tiptap/extension-code'
import { Image } from '@tiptap/extension-image'
import { Table, TableCell, TableHeader, TableRow } from '@tiptap/extension-table'
import { Link } from '@tiptap/extension-link'
import { TextAlign } from '@tiptap/extension-text-align'
import FontFamily from '@tiptap/extension-font-family'

import { MAMLink } from '@/extensions/MAMLink'
import { MAMImage } from '@/extensions/MAMImage.ts'
import { MAMfigure } from '@/extensions/MAMfigure.ts'
import { Indent } from '@/extensions/Indent.ts'
import { TextDirection } from '@/extensions/TextDirection.ts'
import { SearchAndReplace } from '@/extensions/SearchAndReplace.ts'
import { ClassAttribute } from '@/extensions/ClassAttribute.ts'
import { Div } from '@/extensions/Div.ts' 
import { Paste } from '@/extensions/Paste'
import { SanitizeHtml } from '@/extensions/SanitizeHtml'
import { Placeholder } from '@tiptap/extensions'

import Menubar from './Menubar.vue'
import TableMenu from './TableMenu.vue'
import LinkPopover from './LinkPopover.vue'
import globals from '@/globals.js'
import { TIPTAP_CONFIG, FEATURE_BUNDLES } from '@/config.js'

import MarkdownIt from 'markdown-it';
import TurndownService from 'turndown';

import { Decoration, DecorationSet } from 'prosemirror-view'

import { hasExtension, hasCustomLink } from '@/utils/tiptap'
import cloneDeep from "lodash.clonedeep"

const AVAILABLE_EXTENSIONS = {
  StarterKit,
  ClassAttribute,
  Document,
  Text,
  TextStyleKit,
  Div,
  Paragraph,
  Heading,
  ListItem,
  BulletList,
  OrderedList,
  Blockquote,
  CodeBlock,
  Code,
  Image,
  MAMfigure,
  MAMLink,
  Link,
  Underline,
  Subscript,
  Superscript,
  FontFamily,
  SearchAndReplace,
  Table,
  TableCell,
  TableHeader,
  TableRow,
  MAMImage,
  Indent,
  TextDirection,
  TextAlign,
  Paste,
  SanitizeHtml,
  Markdown,
  Placeholder,
}
export default {
  components: {
    EditorContent,
    Menubar,
    BubbleMenu,
    TableMenu,
    LinkPopover,
  },
  props: {
    config: Object,
  },
  data: () => ({
    editor: null,
    showImageModal: false,
    showLinkModal: false,
    source: null,
    mountOptions: {},
    sourceMode: false,
    editorContent: '',
    spellCheck: false,
    isResizing: false,
    lastY: 0,
    lastX: 0,
    currentWidth: 0,
    currentHeight: 0,
    selectionPath: [],
    extensions: [],
    turndownService: new TurndownService(),
    markdownService: new MarkdownIt(),
    showDecorations: false,
    decorations: DecorationSet.empty, // Store decorations dynamically
    _ignoreNextBlur: false,
  }),
  mounted() {
    this.mountOptions = this.$el?.parentElement.dataset
    this.source = document.getElementById(this.mountOptions.source)
    let content = ``
    if (this.source) {
      content = this.source.value
      this.source.style.display = "none";
    }

    this.extensions = Object.entries(this.tiptapconfig.extensions).map(([key, config]) => {
      return AVAILABLE_EXTENSIONS[key] ? AVAILABLE_EXTENSIONS[key].configure(config) : config;
    });
    if (this.tiptapconfig.placeholder) {
      if (!this.extensions.find(ext => ext.name === 'Placeholder')) {
       // console.log(this.__x('imperia Tiptap "{id}": Adding Placeholder extension.', {id: this.mountOptions.source}));
        this.extensions.push(Placeholder.configure({
          placeholder: this.tiptapconfig.placeholder,
        }));
      } else {
       // console.log(this.__x('imperia Tiptap "{id}": Already set Placeholder extension.', {id: this.mountOptions.source}));

        // The Placeholder extension is already included and configured, possibly with more complex settings
      }
    }
    const vm = this;
    this.editor = new Editor({
      extensions: this.extensions,
      editorProps: {
        attributes: {
          class: 'i-tiptap-content'
        },
        decorations: () => this.decorations, // Use stored decorations
        handleDoubleClickOn: (view, pos, node) => {
          if (this.editor.isActive('figure') || this.editor.isActive('image')) {
            this.showImageModal = true
            return false
          } else if (this.editor.isActive('link')) {
            this.showLinkModal = true
            return false
          }
        },
      },
      content: content,
      onTransaction: ({ editor }) => {
        // this.syncImage()
        if (this.source) {
          this.source.value = this.prettifyHTML(this.editor.getHTML());
        }
        if (this.showDecorations) {
          this.addNodeWrappers(); // Reapply decorations when toggled
        }
      },
      onSelectionUpdate: ({ editor }) => {
        const { selection } = editor.state
        const { from } = selection
        const node = selection.node || selection.$anchor.nodeAfter || selection.$anchor.nodeBefore

        const structure = editor.state.doc.resolve(from)
        this.displayPath(node, structure.path)
      },
      onCreate: ({ editor }) => {
        vm._openImgHandler = () => { vm.showImageModal = true }
        vm._openLinkHandler = () => { vm.showLinkModal = true }

        editor.on('mam:open-image-modal', vm._openImgHandler)
        editor.on('mam:open-link-modal', vm._openLinkHandler)
      },
    })

    if (!window.TIPTAP_EDITOR) window.TIPTAP_EDITOR = {}

    if (window.TIPTAP_EDITOR[this.mountOptions.source]) {
      console.warn(this.__x('There is already a TIPTAP editor with the same ID: {id}', {id: this.mountOptions.source}));
    }
    window.TIPTAP_EDITOR[this.mountOptions.source] = this

    if (this.tiptapconfig.spellcheck) {
      this.spellCheck = this.tiptapconfig.spellcheck;
    }
    if (this.tiptapconfig.resizable) {
      this.resizeEditorEvents();
    }

    window.dispatchEvent(new CustomEvent('tiptap-mounted', { detail: { id: this.mountOptions.source } }))
  },
  computed: {
    tiptapconfig() {
      // deep-clone the incoming config OR the template
      const config = cloneDeep(this.config || TIPTAP_CONFIG);

      // IMPORTANT: deep-clone FEATURE_BUNDLES so nothing inside is shared
      const baseBundles = cloneDeep(FEATURE_BUNDLES);

      // merge user bundles on top (also deep already because config is deep)
      config.bundles = config.bundles
        ? Object.assign(baseBundles, cloneDeep(config.bundles))
        : baseBundles;

      if (!config.extensions) {
        const bundleName =
          config.bundle && config.bundles[config.bundle]
            ? config.bundle
            : "fallback";

        // IMPORTANT: deep-clone the bundle you’re applying
        Object.assign(config, cloneDeep(config.bundles[bundleName]));
      }

      config.id = this.mountOptions.source;
      return config;
    },
    sourceContent: {
      // getter
      get() {
        return this.prettifyHTML(this.editor.getHTML());
      },
      // setter
      set(newValue) {
        this.editorContent = newValue;
      }
    },
    // markdownContent: {
    //   get() {
    //     // Convert HTML to Markdown
    //     return this.turndownService.turndown(this.sourceContent);
    //   },
    //   set(markdown) {
    //     // Convert Markdown to HTML
    //     this.sourceContent = this.markdownService.render(markdown);
    //   }
    // },
    markdownContent: {
      get() {
        if (!this.editor) return ''
        return this.editor.getMarkdown()
      },
      set(markdown) {
        if (!this.editor) return
        // Replace the document from Markdown input
        // this.editor.commands.setContent(markdown, { contentType: 'markdown' })
        this.editorContent = markdown;
      }
    },
    editorWidth() {
      return this.currentWidth || this.tiptapconfig.width || '100vw'
    },
    editorHeight() {
      return this.currentHeight || this.tiptapconfig.height || '100vh'
    },
    getEditor() {
      return this.editor
    },
    linkPopover() {
      return this.editor && this.tiptapconfig.bubbleMenu && hasCustomLink(this.editor);
    },
    tablePopover() {
      return this.editor && this.tiptapconfig.bubbleMenu && hasExtension(this.editor, 'table')
    },
  },
  watch: {
    sourceMode(oldVal, newVal) {
      if (newVal) {
        this.editor.commands.setContent(this.editorContent, { contentType: 'markdown'});
        // this.editor.commands.setContent(markdown, { contentType: 'markdown' })
      } else {
        this.editorContent = this.sourceContent;
      }
    },
  },
  methods: {
    __(str) {
      return globals.I.gt.__(str);
    },
    __x(str, params) {
      return globals.I.gt.__x(str, params)
    },
    prettifyHTML(html) {
      return beautifyHtml(html, {
        indent_size: 2,
        indent_char: " ",
        // Avoid breaking attributes/URLs/directives:
        wrap_line_length: 0, // disable hard wrapping

        preserve_newlines: true,
        max_preserve_newlines: 2,

        // Let js-beautify keep its defaults for "unformatted".
        // (Don't pass unformatted: [] )

        indent_inner_html: true,

        // Never touch inner content of these tags
        // (prevents breaking JS/CSS/JSON-LD/code blocks)
        content_unformatted: [
          "script",
          "style",
          "pre",
          "code",
          "textarea",
          "template",
          "svg",
          "math",
        ],

        // Optional: keep attribute layout stable (no forced wrapping)
        wrap_attributes: "auto",
        wrap_attributes_min_attrs: 999, // effectively disables attr wrapping
      });
    },
    displayPath(node, path) {
      let level = 0
      this.selectionPath = path.reduce((acc, parent, pathIndex) => {
        if (pathIndex % 3 === 0) {
          acc.push({
            name: parent?.type.name,
            pos: parent?.pos ?? null, // Store node position
            level: level,
          })
          level = pathIndex / 3 + 1
        }
        return acc
      }, [])

      this.selectionPath.push({
        name: node?.type.name,
        pos: node?.pos ?? null,
        level: level,
        marks: node?.marks.map((mark) => mark?.type.name),
      })
    },
    selectNode(pos) {
      if (pos !== null && this.editor) {
        this.editor.commands.setTextSelection(pos)
        this.editor.view.focus() // Focus the editor
      }
    },
    beforeUnmount() {
      if (this.editor) {
        if (this._openImgHandler) this.editor.off('mam:open-image-modal', this._openImgHandler)
        if (this._openLinkHandler) this.editor.off('mam:open-link-modal', this._openLinkHandler)
        this.editor.destroy()
      }
    },
    resizeEditorEvents() {
      const resizeIcon = this.$refs.resizeIcon;
      const editorContainer = this.$refs.tiptapWrapper;

      resizeIcon.addEventListener('mousedown', (e) => {
        this.isResizing = true
        this.lastX = e.clientX
        this.lastY = e.clientY
        document.body.style.cursor = 'nwse-resize' // Change cursor to diagonal resize
      })

      document.addEventListener('mousemove', (e) => {
        if (!this.isResizing) return

        const deltaX = e.clientX - this.lastX
        const deltaY = e.clientY - this.lastY

        // Resize width
        const newWidth = editorContainer.offsetWidth + deltaX
        if (newWidth > 300) {
          this.currentWidth = newWidth;
          editorContainer.style.width = `${newWidth}px`
        }

        // Resize height
        const newHeight = editorContainer.offsetHeight + deltaY
        if (newHeight > 200) {
          this.currentHeight = newHeight;
          editorContainer.style.height = `${newHeight}px`
        }

        this.lastX = e.clientX
        this.lastY = e.clientY
      })

      document.addEventListener('mouseup', () => {
        this.isResizing = false
        document.body.style.cursor = '' // Reset cursor
      })
    },
    addNodeWrappers() {
      if (!this.showDecorations) {
        this.decorations = DecorationSet.empty;
        return;
      }

      const state = this.editor.state;
      let decorations = []

      state.doc.descendants((node, pos) => {
        if (!node.isText) {
          decorations.push(
            Decoration.node(pos, pos + node.nodeSize, {
              class: 'node-wrapper',
              'data-node-name': node.type.name,
              'data-pos': pos
            })
          )
        }
      })

      this.decorations = DecorationSet.create(state.doc, decorations)
    },
    toggleDecorations() {
      this.showDecorations = !this.showDecorations;
      this.addNodeWrappers(); // Reapply decorations when toggled
      this.editor.view.dispatch(this.editor.state.tr.setMeta("decorations", true));
    },
    focusToolbar() {
      const toolbarEl = this.$refs.menubar?.$el;
      if (!toolbarEl) return;

      toolbarEl.focus?.();
    },
    focusLink() {
      if (this.tiptapconfig.bubbleMenu && this.editor.isActive('link')) {
        const popoverEl = this.$refs.linkpopover;
        if (!popoverEl) return;
        popoverEl.focus({ activateTrap: true, select: true })
      } else {
        this.showLinkModal = true;
      }
    },
  },
}
</script>

<style lang="scss">
/* Basic editor styles */

body, html {
  font-family: "Roboto Regular";
}

.flex {
  display: flex;
}

.justify-between {
  justify-content: space-between;
}

:root {
  --white: #ffffff;
  --light-gray: #f8f9fa;
  --gray: #dee2e6;
  --gray-1: rgba(61, 37, 20, 0.05);
  --gray-2: rgba(61, 37, 20, 0.08);
  --gray-3: rgba(61, 37, 20, 0.12);
  --gray-4: rgba(53, 38, 28, 0.3);
  --gray-5: rgba(28, 25, 23, 0.6);
  --green: #22c55e;
  --purple: #6a00f5;
  --dark-gray: #495057;
  --black: #212529;
  --blue: #1c385f;
  --primary: #4c6ef5;
  --primary-light: #dbe4ff;
  --accent: #15aabf;
  --accent-light: #e3fafc;
  --border-radius: 8px;
  --shadow: rgba(0, 0, 0, 0.1);
}

.i-tiptap-wrapper {
  min-height: 300px;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--gray);
  background-color: var(--light-gray);

  .tiptap {
    padding: 15px;
    border: none;
    outline: none;
    width: 100%;
    height: 100%;
  }
}

.button-group {
  padding: 5px;
  display: flex;
  flex-wrap: wrap;

  .i-button {
    background-color: var(--white);
    border-radius: var(--border-radius);
    color: var(--dark-gray);
    font-size: 14px;
    padding: 5px !important;
    margin: 2px !important;
    cursor: pointer;
    display: flex;
    align-items: center;
    transition:
      background-color 0.2s ease,
      color 0.2s ease;

    &:not(:disabled):hover {
      border: 1px solid var(--blue);
      background-color: var(--primary-light);
      color: var(--primary);
    }

    &.active {
      box-shadow: none;
      border: 1px solid var(--blue);
      background-color: var(--blue);
      color: var(--white);
    }

    i {
      line-height: 14px;
      font-size: 14px;
    }

    span {
      margin-left: 4px;
    }

    span.sr-only {
      display: none;
    }
  }
}

.i-tiptap-content-wrapper {
  overflow-y: hidden;
  background-color: #fff;
  width: 100%;
  height: 100%;
  min-height: 150px;
}

.i-tiptap-content {
  width: 100%;
  background-color: var(--white);
  border: none;
  border-top: 1px solid var(--gray);
  padding: 1rem;
  min-height: 150px;
  overflow: auto;

  em {
    font-style: italic;
  }

  ul {
    list-style-type: disc;
  }

  ol {
    list-style-type: decimal;
  }

  /* List styles */
  ul,
  ol {
    padding: 0 1rem;
    margin: 1.25rem 1rem 1.25rem 0.4rem;

    li p {
      margin-top: 0.25em;
      margin-bottom: 0.25em;
    }
  }

  .ProseMirror {
    outline: none;
    line-height: 1.6;
    font-size: 16px;
    color: var(--dark-gray);
  }
  .ProseMirror-selectednode {
    border: 1px solid var(--gray-2);
    margin: 2rem 0;
  }

  .is-editor-empty:first-child::before {
    color: #adb5bd;
    content: attr(data-placeholder);
    float: left;
    height: 0;
    pointer-events: none;
  }
}

.i-tiptap-footer {
  min-height: 41px;
  position: relative;
  bottom: 0;
  border-top: 1px solid var(--gray);
  background-color: var(--light-gray);
  width: 100%;
  display: flex;
  justify-content: flex-end; /* Align content to the right */
  cursor: pointer;

  .selection_path {
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--dark-gray);
    font-size: 12px;
    text-align: left;
    margin-right: auto;

    .node {
      font-weight: 800;
      margin-left: 10px;
    }
  }

  .resize-icon {
    position: absolute;
    bottom: 0px;
    color: var(--dark-gray);
    font-size: 16px;
    user-select: none;
    margin-right: 4px;
    cursor: nwse-resize; /* Change cursor to indicate resize */
  }
}

.search-highlight {
  background-color: yellow;
}
.search-highlight-active {
  background-color: orange;
  border: 1px solid red;
}

/* Table-specific styling */
.i-tiptap-wrapper table {
  border-collapse: collapse;
  margin: 0;
  overflow: hidden;
  table-layout: fixed;

  td,
  th {
    border: 1px solid var(--gray-3);
    box-sizing: border-box;
    min-width: 1em;
    padding: 6px 8px;
    position: relative;
    vertical-align: top;

    > * {
      margin-bottom: 0;
    }
  }

  th {
    background-color: var(--gray-1);
    font-weight: bold;
    text-align: left;
  }

  .selectedCell:after {
    background: var(--gray-2);
    content: '';
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    pointer-events: none;
    position: absolute;
    z-index: 2;
  }

  .column-resize-handle {
    background-color: var(--purple);
    bottom: -2px;
    pointer-events: none;
    position: absolute;
    right: -2px;
    top: 0;
    width: 4px;
  }
}

textarea.html-source {
  height: 100%;
  font-family: "Courier New", monospace;
  font-size: 14px;
  line-height: 1.5;
  white-space: pre;
  overflow: auto;
  background-color: #282c34; /* Dark theme */
  color: #f8f8f2; /* Light text */
  border: 1px solid #444;
  border-radius: 5px;
  outline: none;
  resize: none;
}

/* Optional: Scrollbar styling */
textarea.html-source::-webkit-scrollbar {
  width: 8px;
}

textarea.html-source::-webkit-scrollbar-thumb {
  background: #555;
  border-radius: 4px;
}

textarea.html-source::-webkit-scrollbar-thumb:hover {
  background: #777;
}

/* WebKit browsers (Chrome, Safari, Opera) */
textarea.html-source::selection {
  color: #fff;            /* text color when selected */
  background: #008000;    /* highlight color */
}

/* Firefox */
textarea.html-source::-moz-selection {
  color: #fff;
  background: #008000;
}

.node-wrapper {
  border: 1px dashed gray;
  position: relative;
  padding: 25px;
  margin-top: 25px;
  margin-bottom: 25px;
}

.node-wrapper::before {
  content: attr(data-node-name);
  position: absolute;
  top: -10px;
  left: 5px;
  background: white;
  font-size: 12px;
  padding: 2px;
  color: gray;
}
</style>

